import { useEffect, useRef, type ReactNode } from 'react';
import { Button } from './Button';

interface ConfirmDialogProps {
  titulo: string;
  children?: ReactNode;
  confirmar: string;
  cancelar?: string;
  onConfirmar: () => void;
  onCancelar: () => void;
}

/**
 * Diálogo modal de confirmación. Dibujado en la página (no `window.confirm`)
 * para que respete el estilo y no bloquee el hilo. Escape cancela; el foco
 * arranca en "Cancelar", la opción segura.
 */
export function ConfirmDialog({
  titulo,
  children,
  confirmar,
  cancelar = 'Cancelar',
  onConfirmar,
  onCancelar,
}: ConfirmDialogProps) {
  const cancelarRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelarRef.current?.focus();
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancelar();
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [onCancelar]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-titulo"
        className="w-full max-w-sm space-y-4 rounded-2xl bg-white p-6 shadow-lg"
      >
        <h2 id="confirm-titulo" className="text-lg font-semibold text-slate-900">
          {titulo}
        </h2>
        {children && <div className="text-sm text-slate-600">{children}</div>}
        <div className="flex justify-end gap-2">
          <Button ref={cancelarRef} variant="secondary" onClick={onCancelar}>
            {cancelar}
          </Button>
          <Button onClick={onConfirmar}>{confirmar}</Button>
        </div>
      </div>
    </div>
  );
}
